import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, X, Loader2 } from "lucide-react";
import { pushSupported, isPushSubscribed, subscribeWebPush } from "@/lib/webpush";
import { requestNotifyPermission } from "@/lib/notify";

const DISMISS_KEY = "imperium-push-prompt-dismissed";

// Non-intrusive dashboard banner nudging the client to enable push notifications
// so they're alerted the moment their file is ready. Hidden once enabled or dismissed.
export const PushPromptBanner = () => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* noop */ }
    if (!pushSupported() || dismissed) return;
    isPushSubscribed().then((sub) => { if (!sub) setShow(true); });
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if (Notification.permission !== "granted") {
        const r = await requestNotifyPermission();
        if (r !== "granted") { toast.error("Autorisez les notifications dans votre navigateur."); return; }
      }
      const ok = await subscribeWebPush();
      if (ok) { toast.success("Notifications activées 🔔"); setShow(false); }
      else toast.error("Activation impossible sur cet appareil.");
    } finally { setBusy(false); }
  };

  const close = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  if (!show) return null;

  return (
    <div data-testid="push-prompt-banner"
      className="glass rounded-md p-4 mb-6 border border-aurora-cyan/25 flex items-center gap-4">
      <span className="w-10 h-10 shrink-0 grid place-items-center rounded-sm bg-aurora-cyan/12 text-aurora-cyan">
        <BellRing className="w-5 h-5" />
      </span>
      <p className="min-w-0 flex-1 text-sm text-zinc-300">
        Activez les notifications pour être prévenu dès que votre fichier est prêt.
      </p>
      <button onClick={enable} disabled={busy} data-testid="push-prompt-enable"
        className="shrink-0 flex items-center gap-1.5 px-4 h-9 rounded-sm bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs hover:brightness-110 transition-all disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />} Activer
      </button>
      <button onClick={close} data-testid="push-prompt-close" aria-label="Fermer"
        className="shrink-0 w-8 h-8 grid place-items-center rounded-sm text-zinc-500 hover:text-white transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
